import { useSyncExternalStore } from 'react';
import { SMS_BASE_URI, SMS_QUOTE_LINK } from '../constants';

// B02 — lightweight enquiry capture shared between the quote form and the
// SMS controls (hero / sticky bar / band / footer / drawer / photo help).
// One in-memory draft, written ONLY by QuoteForm; readers rebuild the SMS
// body from it so a draft always reflects the user's CURRENT selection
// (a corrected service wins over the route's initial one). Nothing here is
// persisted or sent to analytics.

// Backend contract (B00): QuoteRequestCreate.message max_length = 1500.
// The FINAL composed message (labels + job text) is validated against this
// before POST — never truncated silently, never an opaque 422.
export const MESSAGE_MAX = 1500;

export const ENQUIRY_SERVICES = [
  {
    key: 'carpentry',
    label: 'General carpentry',
    guidance: 'Describe what needs building, replacing or repairing, your suburb and any access details. Rough dimensions and photos can help.',
    sms: (d) => `Hi Corey, I need carpentry work in ${d.suburb || '[suburb]'}. The job is ${d.details || '[details]'}. I can send photos.`,
  },
  {
    key: 'renovations',
    label: 'Renovations and extensions',
    guidance: 'Tell me which rooms or areas you want to change, the stage of your plans and your preferred timing. Mention any plans or approvals already in place.',
    sms: (d) => `Hi Corey, I would like to discuss a renovation in ${d.suburb || '[suburb]'}. My plans are ${d.details || '[details]'}. I can send photos or plans.`,
  },
  {
    key: 'outdoor',
    label: 'Decks, fences and outdoor carpentry',
    guidance: 'Tell me whether this is a new build or a repair, the rough size, materials you have in mind and any access details.',
    sms: (d) => `Hi Corey, I need help with decks, fences or outdoor carpentry in ${d.suburb || '[suburb]'}. The job is ${d.details || '[details]'}. I can send photos.`,
  },
  {
    key: 'repairs',
    label: 'Repairs and maintenance',
    guidance: 'Include the full job list, number of items and any access details.',
    sms: (d) => `Hi Corey, I need help with repairs in ${d.suburb || '[suburb]'}. My full job list is ${d.details || '[details]'}. I can send photos.`,
  },
  {
    key: 'doors',
    label: 'Door repairs and installation',
    guidance: 'Tell us how many doors, whether they are internal or external, and what is wrong. Include any door or hardware you plan to supply.',
    sms: (d) => `Hi Corey, I need help with [number/type of doors] in ${d.suburb || '[suburb]'}. The problem is ${d.details || '[details]'}. I can send photos.`,
  },
  {
    key: 'interiors',
    label: 'Custom interiors and built-ins',
    guidance: 'Describe what you want to build or store, the room and any rough dimensions. Mention existing fittings or lighting that may affect the work.',
    sms: (d) => `Hi Corey, I'd like to discuss ${d.design || '[shelving, built-ins or interior work]'} in ${d.suburb || '[suburb]'}. My idea is ${d.details || '[details]'}. I can send photos and rough dimensions.`,
  },
];

export const SERVICE_BY_KEY = ENQUIRY_SERVICES.reduce((acc, s) => { acc[s.key] = s; return acc; }, {});

// Route → initial (correctable) service.
export const ROUTE_SERVICE = {
  '/carpenter-hobart': 'carpentry',
  '/renovations-hobart': 'renovations',
  '/decks-and-fences-hobart': 'outdoor',
  '/handyman-hobart': 'repairs',
  '/doors-hobart': 'doors',
  '/custom-interiors-hobart': 'interiors',
};

export const PHOTO_HELP = 'Photos are optional. You can text them to 0434 727 099 with your name and suburb.';

// Whitespace-only optional values are treated as absent.
export const clean = (v) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim() : '');

// Final message delivered ONCE through the existing backend formatter —
// labels first, then the visitor's own text (trimmed, otherwise untouched).
export const composeMessage = ({ serviceLabel, suburb, timing, message }) => {
  const lines = [];
  if (serviceLabel) lines.push(`Service: ${serviceLabel}`);
  if (clean(suburb)) lines.push(`Suburb: ${clean(suburb)}`);
  if (clean(timing)) lines.push(`Preferred timing: ${clean(timing)}`);
  const body = (message || '').trim();
  return lines.length ? `${lines.join('\n')}\n\n${body}` : body;
};

// ── draft store ────────────────────────────────────────────────────────────
const EMPTY = { service: '', suburb: '', timing: '', message: '', design: '' };
let draft = EMPTY;
const subs = new Set();
const emit = () => subs.forEach((fn) => fn());
export const setEnquiryDraft = (patch) => { draft = { ...draft, ...patch }; emit(); };
export const resetEnquiryDraft = () => { draft = EMPTY; emit(); };
const subscribe = (fn) => { subs.add(fn); return () => subs.delete(fn); };
const get = () => draft;
export const useEnquiryDraft = () => useSyncExternalStore(subscribe, get, get);

// SMS body for the draft — the SELECTED service decides the template. With
// no service chosen ("Not sure yet") the entered job, suburb and timing are
// STILL carried in a generic template (root-found defect: the empty branch
// used to drop all context); only when nothing at all has been entered does
// it fall back to the sitewide generic quote text. Blank values are
// omitted / left as editable [prompts]; empty timing is omitted entirely.
// Opens a draft in the Messages app — never sends.
export const buildEnquirySms = (d) => {
  const suburb = clean(d.suburb);
  const timing = clean(d.timing);
  const details = (d.message || '').trim();
  const svc = SERVICE_BY_KEY[d.service];
  let body;
  if (svc) {
    body = svc.sms({ suburb, details, design: clean(d.design) });
  } else {
    if (!suburb && !timing && !details) return SMS_QUOTE_LINK;
    body = `Hi Corey, I'd like a quote for ${details || '[details]'} in ${suburb || '[suburb]'}. I can send photos.`;
  }
  if (timing) body += ` Preferred timing: ${timing}.`;
  return SMS_BASE_URI + encodeURIComponent(body);
};

export const useEnquirySmsHref = () => buildEnquirySms(useEnquiryDraft());
